// Utilitários puros da ficha do merchant — sem React, testáveis isoladamente.
import type {
  AdminMerchantPaymentMethod,
  MerchantPaymentMethodName,
} from "@/types/admin-merchant"
import type { PaymentMethodCatalogItem } from "@/types/payment-method-catalog"

// O catálogo do que dá para habilitar vem do backoffice
// (GET /payment-methods → linhas ativas de payment_method). Era uma constante
// aqui, que divergiu do enum do backend e do catálogo do white label — e
// oferecia WALLET, que nem existe em PaymentMethods.

/** CNPJ 00.000.000/0000-00 · CPF 000.000.000-00. Valor fora do padrão sai como veio. */
export const formatDocument = (
  document?: string | null
): string => {
  if (!document) return "—"
  const digits = document.replace(/\D/g, "")
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    )
  }
  if (digits.length === 11) {
    return digits.replace(
      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
      "$1.$2.$3-$4"
    )
  }
  return document
}

/** CEP 00000-000. Valor fora do padrão sai como veio. */
export const formatZipcode = (
  zipcode?: string | null
): string => {
  if (!zipcode) return "—"
  const digits = zipcode.replace(/\D/g, "")
  return digits.length === 8
    ? digits.replace(/^(\d{5})(\d{3})$/, "$1-$2")
    : zipcode
}

/** Duas primeiras letras (ignora pontuação) para o avatar do card. */
export const merchantInitials = (name?: string | null) =>
  (name ?? "")
    .replace(/[^\p{L}\p{N}]/gu, "")
    .slice(0, 2)
    .toUpperCase() || "--"

/**
 * Métodos do catálogo ainda NÃO habilitados — alimenta a seção "adicionar
 * método". Já habilitado porém inativo também fica de fora: readicionar daria
 * 409 (a UK é por par loja+método); reativar é PUT.
 */
export const availableMethodsToAdd = (
  configured: AdminMerchantPaymentMethod[],
  catalog: PaymentMethodCatalogItem[] = []
): MerchantPaymentMethodName[] => {
  const used = new Set(
    configured.map((item) => item.paymentMethod)
  )
  return catalog
    .map((item) => item.paymentMethod)
    .filter((method) => !used.has(method))
}

/** Percentual exibido no resumo do método (2 casas, locale pt-BR). */
export const formatPercentage = (
  value?: number | null
): string =>
  typeof value === "number"
    ? `${new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)}%`
    : "—"

/** Valor monetário do método (BRL). */
export const formatCurrency = (
  value?: number | null
): string =>
  typeof value === "number"
    ? new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value)
    : "—"
